import {
  LinkProps as ChakraLinkProps,
  Link as ChakraLink,
} from "@chakra-ui/react";
import NextLink from "next/link";
import {
  FC,
  forwardRef,
  MouseEventHandler,
  Ref,
  RefAttributes,
  useCallback,
} from "react";

import { track } from "lib/analytics";

function isOutboundHref(href?: string): boolean {
  if (!href) return false;
  return /^https?:\/\//.test(href) && !href.includes("hightouch.com");
}

function isAppHref(href?: string): boolean {
  if (!href) return false;
  return href.includes("app.hightouch.com");
}

function extractLinkText(children: unknown): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  return "";
}

export type LinkProps = {
  href?: string;
  newTab?: boolean;
  /** @default: "primary" */
  variant?: "primary" | "secondary" | "inline" | "subtle";
  onClick?: MouseEventHandler;
  onMouseMove?: MouseEventHandler;
} & RefAttributes<HTMLAnchorElement> &
  Omit<
    ChakraLinkProps,
    "href" | "target" | "rel" | "variant" | "textDecoration"
  >;

export const Link: FC<Readonly<LinkProps>> = forwardRef(
  (
    { href, newTab, children, variant = "primary", onClick, ...props },
    ref: Ref<HTMLAnchorElement>
  ) => {
    const handleClick: MouseEventHandler = useCallback(
      (e) => {
        if (isOutboundHref(href)) {
          track("Outbound Link Clicked", {
            page_path: window.location.pathname,
            link_url: href,
            link_text: extractLinkText(children),
          });
        } else if (href?.startsWith("/") && !href?.startsWith("#")) {
          track("Internal Link Clicked", {
            page_path: window.location.pathname,
            link_url: href,
            link_text: extractLinkText(children),
          });
        }
        onClick?.(e);
      },
      [href, children, onClick]
    );

    const opensNewTab = newTab || isAppHref(href);

    const LinkElement = (
      <ChakraLink
        ref={ref}
        href={href}
        target={opensNewTab ? "_blank" : undefined}
        rel={opensNewTab ? "noreferrer noopener" : undefined}
        variant={variant}
        onClick={handleClick}
        {...props}
      >
        {children}
      </ChakraLink>
    );

    if (href?.startsWith("/")) {
      return (
        <NextLink passHref href={href}>
          {LinkElement}
        </NextLink>
      );
    }

    return LinkElement;
  }
);

Link.displayName = "Link";
